import { useEffect, useRef } from 'react'
import TagManager from 'react-gtm-module'
import { useLocation } from 'react-router-dom'

import { sessionInfoSelectors } from '@leanpay/models'

import { isMarketRom, isMarketSlo } from 'library/constants'
import { useGetSessionInfo } from 'library/hooks/query-hooks'

const romPathToEvent: Record<string, string> = {
    '/check-limit': 'pq_step1_check_limit',
    '/request-otp': 'pq_step2_phone',
    '/login': 'pq_step3_verification',
    '/personal-details': 'pq_step4_form_submit',
    '/financial-details': 'pq_step5_fin_details',
    '/address-details': 'pq_step6_address_details',
    '/two-fa-pin': 'pq_step7_pin',
    '/validate-offer': 'pq_step8_preview',
    '/prequalified-success': 'pq_step9_activate_limit',
    '/prequalified-error': 'pq_step9_rejected_limit',
}

const sloPathToEvent: Record<string, string> = {
    '/check-limit': 'pq_step1_check_limit',
    '/request-otp': 'pq_step2_phone',
    '/login': 'pq_step3_verification',
    '/personal-details': 'pq_step4_form_submit',
    '/two-fa-pin': 'pq_step5_pin',
    '/financial-details': 'pq_step6_fin_details',
    '/preview-financial-details': 'pq_step7_fin_preview',
    '/prequalified-success': 'pq_step8_activate_limit',
    '/prequalified-error': 'pq_step8_rejected_limit',
}

const isActivateLimitEvent = (event: string): boolean =>
    event === 'pq_step8_activate_limit' || event === 'pq_step9_activate_limit'

const isRejectedLimitEvent = (event: string): boolean =>
    event === 'pq_step8_rejected_limit' || event === 'pq_step9_rejected_limit'

export const useTrackPqStep = (): void => {
    const location = useLocation()
    const { data: sessionInfo } = useGetSessionInfo()
    const lastFiredPathRef = useRef<string | null>(null)
    const lastFiredEventRef = useRef<string | null>(null)

    useEffect(() => {
        const pathToEvent = isMarketSlo ? sloPathToEvent : isMarketRom ? romPathToEvent : null

        if (!pathToEvent) return

        const event = pathToEvent[location.pathname]
        if (!event) return

        if (lastFiredPathRef.current === location.pathname) return

        if (
            lastFiredEventRef.current &&
            isActivateLimitEvent(lastFiredEventRef.current) &&
            isRejectedLimitEvent(event)
        ) {
            lastFiredPathRef.current = location.pathname
            return
        }

        lastFiredPathRef.current = location.pathname
        lastFiredEventRef.current = event

        const isFinalStepEvent = isActivateLimitEvent(event) || isRejectedLimitEvent(event)

        TagManager.dataLayer({
            dataLayer: isFinalStepEvent
                ? {
                      event,
                      newCustomer: !(
                          sessionInfo && sessionInfoSelectors.isReturningCustomer(sessionInfo)
                      ),
                  }
                : { event },
        })
    }, [location.pathname, sessionInfo])
}
